import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useModuleByIdQuery, useRestoreModuleMutation } from '@entities/Module';
import { CramPicker, readBuilderDefaults } from '@features/CramPicker';
import { buildUnitPath, paths, type WordsReturnState } from '@shared/config';
import { Button, toast } from '@shared/ui';

export default function CramPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // «Разминка» на 10 карточек без финального матча из ссылки письма (/repeat) — читается при заходе.
  const [defaults] = useState(() => readBuilderDefaults(searchParams));
  const query = useModuleByIdQuery(id);
  const restore = useRestoreModuleMutation();
  const module = query.data;
  const entry = location.state as { fromDashboard?: boolean; fromWords?: boolean } | null;
  const fromDashboard = entry?.fromDashboard ?? false;
  // «Учить» на «Словах и модулях» — назад и после тренировки туда же (владелец 2026-10-28: «хотелось вернуться на
  // слова и модули», а не в модуль).
  const fromWords = entry?.fromWords ?? false;

  if (query.isPending) return <div className="mx-auto mt-20 h-80 max-w-[640px] animate-skeleton rounded-card bg-card" />;
  // Удалённый модуль лежит в «Удалённых» бессрочно: ссылка из старого письма или закладка ведёт
  // не в тупик, а к восстановлению — после него здесь же открывается тренировка.
  if (module?.deleted) {
    const handleRestore = () => {
      if (restore.isPending) return;
      restore.mutate(module.id, {
        onSuccess: () => {
          toast({
            tone: 'success',
            title: t('moduleDetail.restore.toastTitle'),
            message: t('moduleDetail.restore.toastBody', { name: module.title }),
          });
          void query.refetch();
        },
      });
    };
    return (
      <div className="mx-auto max-w-[960px] px-4 pt-16 text-center">
        <p role="alert" className="mx-auto mb-4 max-w-[480px] text-muted">
          {t('moduleDetail.restore.inTraining', { name: module.title })}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button onClick={handleRestore} disabled={restore.isPending}>
            {restore.isPending ? '…' : t('moduleDetail.restore.cta')}
          </Button>
          <Button variant="ghost" onClick={() => navigate(paths.training)}>
            {t('training.chooseModule')}
          </Button>
        </div>
        {restore.isError && <p className="mt-3 text-[12px] text-danger">{t('moduleDetail.restore.error')}</p>}
      </div>
    );
  }
  if (query.isError || !module) {
    return (
      <div className="mx-auto max-w-[960px] px-4 pt-16 text-center">
        <p role="alert" className="mb-4 text-muted">
          {t(query.isError ? 'training.loadError' : 'moduleDetail.notFound')}
        </p>
        <Button onClick={() => (query.isError ? void query.refetch() : navigate(paths.training))}>
          {t(query.isError ? 'training.retry' : 'training.chooseModule')}
        </Button>
      </div>
    );
  }
  return (
    <CramPicker
      module={module}
      onBack={() =>
        fromWords
          ? navigate(paths.words, { state: { wordsModuleId: module.id } satisfies WordsReturnState })
          : navigate(fromDashboard ? paths.training : buildUnitPath(module.id))
      }
      returnTo={fromWords ? 'words' : fromDashboard ? 'dashboard' : 'module'}
      defaults={defaults}
    />
  );
}
